import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';

const DataImport = () => {
  const [importData, setImportData] = useState('');
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);

  const handleImport = async () => {
    if (!importData.trim()) {
      alert('Please enter work order data to import');
      return;
    }

    setImporting(true);
    try {
      // Parse the input data (expecting JSON format)
      const workOrders = JSON.parse(importData);
      
      const backendUrl = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/api/projects/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ work_orders: workOrders })
      });

      if (response.ok) {
        const result = await response.json();
        setResults(result);
        alert(`Import completed! ${result.imported} imported, ${result.skipped} skipped`);
      } else {
        throw new Error('Import failed');
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Import failed: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  const sampleData = `[
  {
    "work_order": "25-7002",
    "date": "2024-11-01",
    "customer": "Fike Canada",
    "quote_number": "23-171R2",
    "description": "Ashbridges Bay Water Treatment Plant - Fire Suppression System Installation",
    "contact": "N/A",
    "estimator": "JUSTIN KOLESZAR",
    "po_number": "",
    "subtotal": 13648.15,
    "tax": 2339.99,
    "invoice_number": "",
    "invoice_amount": 0.0,
    "status": "in_progress",
    "date_sent": null,
    "days_since_invoice": 0,
    "terms": "Net 30 Days"
  }
]`;

  return (
    <div className="p-6 ops-canvas text-slate-900 min-h-screen">
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-900">Work Order Data Import</CardTitle>
          <p className="text-slate-500">Import work orders from your spreadsheet data</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Work Order Data (JSON format)
              </label>
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder={sampleData}
                className="w-full h-64 px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900 resize-none font-mono text-sm"
              />
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={handleImport}
                disabled={importing}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg"
              >
                {importing ? 'Importing...' : 'Import Work Orders'}
              </button>
              
              <button
                onClick={() => setImportData(sampleData)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-lg"
              >
                Load Sample Data
              </button>
              
              <button
                onClick={() => setImportData('')}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
              >
                Clear
              </button>
            </div>

            {results && (
              <div className="mt-4 p-4 bg-slate-100 rounded-lg">
                <h3 className="text-lg font-semibold text-green-700 mb-2">Import Results</h3>
                <div className="space-y-1">
                  <p>✅ Imported: {results.imported}</p>
                  <p>⚠️ Skipped (already exists): {results.skipped}</p>
                  <p>📊 Total processed: {results.total}</p>
                </div>
              </div>
            )}

            <div className="mt-6 p-4 bg-slate-100 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Instructions</h3>
              <div className="space-y-2 text-sm text-slate-700">
                <p>1. Format your spreadsheet data as JSON (use the sample format above)</p>
                <p>2. Ensure work_order field follows format: "25-XXXX"</p>
                <p>3. Date should be in YYYY-MM-DD format</p>
                <p>4. The system will automatically skip duplicates based on work_order number</p>
                <p>5. All projects will be created with status "in_progress" by default</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataImport;